// src/pages/PageNotFound.jsx

import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Home,
  Leaf,
  Sprout,
} from "lucide-react";

export default function PageNotFound() {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#f6fbf8] px-5 py-10">

      {/* =====================================================
          BACKGROUND GLOW
      ===================================================== */}

      <div className="absolute -left-32 -top-32 h-96 w-96 animate-pulse rounded-full bg-green-100/70 blur-3xl" />

      <div
        className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-emerald-100/70 blur-3xl"
        style={{
          animation: "pulse 4s ease-in-out infinite",
        }}
      />


      {/* =====================================================
          FLOATING LEAVES
      ===================================================== */}

      <div
        className="absolute left-[8%] top-[15%] text-[#16854f]/20"
        style={{
          animation: "float 5s ease-in-out infinite",
        }}
      >
        <Leaf size={55} />
      </div>

      <div
        className="absolute right-[10%] top-[20%] text-[#16854f]/20"
        style={{
          animation: "float 6s ease-in-out infinite 1s",
        }}
      >
        <Leaf size={75} />
      </div>

      <div
        className="absolute bottom-[15%] left-[12%] text-[#16854f]/15"
        style={{
          animation: "float 7s ease-in-out infinite 0.5s",
        }}
      >
        <Sprout size={80} />
      </div>

      <div
        className="absolute bottom-[18%] right-[12%] text-[#16854f]/15"
        style={{
          animation: "float 6s ease-in-out infinite 1.5s",
        }}
      >
        <Leaf size={60} />
      </div>


      {/* =====================================================
          MAIN CARD
      ===================================================== */}

      <div
        className="relative z-10 w-full max-w-[850px]"
        style={{
          animation: "cardEnter 0.8s ease-out forwards",
        }}
      >

        <div className="rounded-[32px] border border-green-100 bg-white p-8 text-center shadow-[0_25px_80px_rgba(0,80,50,0.12)] sm:p-12 lg:p-16">


          {/* =================================================
              ANIMATED ICON
          ================================================= */}

          <div
            className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-50 text-[#087f4f]"
            style={{
              animation: "iconFloat 3s ease-in-out infinite",
            }}
          >
            <Leaf
              size={40}
              strokeWidth={1.8}
            />
          </div>


          {/* =================================================
              404
          ================================================= */}

          <div className="relative mt-8">

            {/* Glow */}
            <div className="absolute left-1/2 top-1/2 h-32 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-200/30 blur-3xl" />

            <h1
              className="relative text-[90px] font-black leading-none tracking-tight text-[#087f4f] sm:text-[130px]"
              style={{
                animation: "numberFloat 4s ease-in-out infinite",
              }}
            >
              404
            </h1>

          </div>


          {/* =================================================
              HEADING
          ================================================= */}

          <h2
            className="mt-5 text-3xl font-extrabold text-[#10231c] sm:text-4xl"
            style={{
              animation: "fadeUp 0.8s ease-out 0.3s both",
            }}
          >
            Page Not Found
          </h2>


          {/* =================================================
              DESCRIPTION
          ================================================= */}

          <p
            className="mx-auto mt-4 max-w-xl text-base leading-7 text-gray-500"
            style={{
              animation: "fadeUp 0.8s ease-out 0.45s both",
            }}
          >
            Looks like this page has grown somewhere else.
            The page you're looking for doesn't exist or may
            have been moved.
          </p>


          {/* =================================================
              GREEN MESSAGE
          ================================================= */}

          <div
            className="mx-auto mt-7 inline-flex items-center gap-2 rounded-full bg-green-50 px-5 py-2.5 text-sm font-semibold text-[#087f4f]"
            style={{
              animation: "fadeUp 0.8s ease-out 0.6s both",
            }}
          >
            <Sprout
              size={17}
              style={{
                animation: "sprout 2s ease-in-out infinite",
              }}
            />

            Let's get you back to growing
          </div>


          {/* =================================================
              BUTTONS
          ================================================= */}

          <div
            className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
            style={{
              animation: "fadeUp 0.8s ease-out 0.75s both",
            }}
          >

            {/* BACK BUTTON */}

            <button
              onClick={() => window.history.back()}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 px-7 py-3.5 text-sm font-bold text-gray-700 transition-all duration-300 hover:-translate-y-1 hover:border-[#087f4f] hover:text-[#087f4f] hover:shadow-md sm:w-auto"
            >

              <ArrowLeft
                size={17}
                className="transition-transform duration-300 group-hover:-translate-x-1"
              />

              Go Back

            </button>


            {/* HOME BUTTON */}

            <Link
              to="/"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#087f4f] px-7 py-3.5 text-sm font-bold text-white shadow-md transition-all duration-300 hover:-translate-y-1 hover:bg-[#066b42] hover:shadow-xl sm:w-auto"
            >

              <Home
                size={17}
                className="transition-transform duration-300 group-hover:scale-110"
              />

              Back to Home

              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />

            </Link>

          </div>


          {/* =================================================
              BRAND
          ================================================= */}

          <div
            className="mt-12 border-t border-gray-100 pt-7"
            style={{
              animation: "fadeUp 0.8s ease-out 0.9s both",
            }}
          >

            <div className="flex items-center justify-center gap-2">

              <div
                className="flex h-9 w-9 items-center justify-center rounded-full bg-green-100 text-[#087f4f]"
                style={{
                  animation: "spinSlow 8s linear infinite",
                }}
              >
                <Leaf size={18} />
              </div>

              <span className="text-sm font-bold tracking-wide text-[#087f4f]">
                POLYBIONIC TECHNOLOGIES
              </span>

            </div>

            <p className="mt-2 text-xs text-gray-400">
              Technology for a Sustainable Future
            </p>

          </div>

        </div>

      </div>


      {/* =====================================================
          CUSTOM ANIMATIONS
      ===================================================== */}

      <style>
        {`
          @keyframes float {
            0%,
            100% {
              transform: translateY(0px) rotate(0deg);
            }

            50% {
              transform: translateY(-25px) rotate(8deg);
            }
          }

          @keyframes iconFloat {
            0%,
            100% {
              transform: translateY(0px);
            }

            50% {
              transform: translateY(-8px);
            }
          }

          @keyframes numberFloat {
            0%,
            100% {
              transform: translateY(0px) scale(1);
            }

            50% {
              transform: translateY(-8px) scale(1.02);
            }
          }

          @keyframes cardEnter {
            0% {
              opacity: 0;
              transform: translateY(40px) scale(0.96);
            }

            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes fadeUp {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }

            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes sprout {
            0%,
            100% {
              transform: rotate(0deg) scale(1);
            }

            50% {
              transform: rotate(-8deg) scale(1.1);
            }
          }

          @keyframes spinSlow {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            *,
            *::before,
            *::after {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }
        `}
      </style>

    </div>
  );
}